import {
    assign,
    get,
    isEmpty,
    isNumber,
    makeArray,
    map,
    omitBy,
    regionUtils,
    toFixed
} from '~/utils';
import { HELP_SOURCE_JDY, HELP_SOURCE_JODOO } from '~/locales/help-source';
import Intl, { LOCALE_KEY } from '@fx-ui/intl';
import { PRODUCT_ENV, Region } from '~/interfaces';
import { helpSourceIntl, imageSourceIntl, LOCALE_IMAGE } from '~/constants';

export const loadLocale = (locale: LOCALE_KEY) => {
    return import(/* webpackChunkName: "[request]" */ `~/locales/${locale}.json`).then(
        (m) => m.default
    );
};

export type ILocales = (locale: LOCALE_KEY) => Promise<Record<string, string>>;

const getHelpSourceLocale = () => {
    let HELP_SOURCE_LOCALE: any = {};
    switch (APP_ENV) {
        case PRODUCT_ENV.JDY:
        case PRODUCT_ENV.SELFHOSTED:
            HELP_SOURCE_LOCALE = HELP_SOURCE_JDY;
            break;
        case PRODUCT_ENV.JODOO:
            HELP_SOURCE_LOCALE = HELP_SOURCE_JODOO;
            break;
        default:
            break;
    }
    return HELP_SOURCE_LOCALE;
};
/**
 * 帮助文档国际化
 *
 * @param currentLocale
 */
export const helpSourceIntlInit = (currentLocale: LOCALE_KEY) => {
    const HELP_SOURCE_LOCALE = getHelpSourceLocale();

    /**
     * jodoo环境下，非英文环境时，空帮助文档链接默认拿英文文档链接兜底
     * @see FXD-64509
     */
    if (APP_ENV === PRODUCT_ENV.JODOO && currentLocale !== LOCALE_KEY.EN_US) {
        assign(HELP_SOURCE_LOCALE, {
            [currentLocale]: assign(
                {},
                HELP_SOURCE_JODOO[LOCALE_KEY.EN_US],
                omitBy(HELP_SOURCE_JODOO[currentLocale], isEmpty)
            )
        });
    }
    if (HELP_SOURCE_LOCALE) {
        helpSourceIntl.init(currentLocale, HELP_SOURCE_LOCALE[currentLocale] || {});
    }
};

/**
 * 图片国际化
 *
 * @param currentLocale
 */
export const localeImageIntlInit = (currentLocale: LOCALE_KEY) => {
    // 除了简体中文，繁体中文，其他语言都使用英文版本
    switch (currentLocale) {
        case LOCALE_KEY.ZH_CN:
        case LOCALE_KEY.ZH_TW:
            imageSourceIntl.init(currentLocale, get(LOCALE_IMAGE, currentLocale, {}));
            break;
        default:
            imageSourceIntl.init(currentLocale, get(LOCALE_IMAGE, LOCALE_KEY.EN_US, {}));
    }
};

/**
 * 文本国际化
 *
 * @param currentLocale
 * @param extraLocales
 */
export const textIntlInit = (currentLocale: LOCALE_KEY, extraLocales?: ILocales | ILocales[]) => {
    return Promise.all([
        loadLocale(currentLocale),
        ...map(makeArray(extraLocales), (v) => v(currentLocale))
    ]).then(([mainLocaleModule, ...extraLocalesModules]) => {
        Intl.init(
            currentLocale,
            assign(mainLocaleModule || {}, ...map(extraLocalesModules, (v) => v || {}))
        );
    });
};

/**
 * 对数字进行缩略，并做国际化
 *
 * 中/日/韩 环境下 使用 万/亿 做缩略
 * 英/俄/阿拉伯文 环境下 使用 K/M/B 做缩略
 *
 * 暂时只实现了中/英
 * @param value
 * @param region
 */
export const numberIntl = (value: number, region: Region) => {
    const isNegative = value < 0;

    const getFormattedNum = (value: number) => {
        const locale = Intl.getLocale();
        // 中日韩都使用 万
        if (locale === 'zh_cn' || locale === 'zh_tw') {
            // 中文的显示方式 万
            if (value < 10000) {
                return value;
            } else if (value < 100000000) {
                return toFixed(value / 10000, 1) + Intl.get('ten.Thousand');
            } else {
                return toFixed(value / 100000000, 1) + Intl.get('one.hundred.million');
            }
        } else {
            // 英文的显示方式  k
            if (value < 1000) {
                return value;
            } else if (value < 1000000) {
                return toFixed(value / 1000, 1) + 'K';
            } else if (value < 1000000000) {
                return toFixed(value / 1000000, 1) + 'M';
            } else {
                return toFixed(value / 1000000000, 1) + 'B';
            }
        }
    };
    const formattedNum = getFormattedNum(Math.abs(value));
    const result = isNumber(formattedNum)
        ? isNegative
            ? 0 - formattedNum
            : formattedNum
        : isNegative
        ? `-${formattedNum}`
        : formattedNum;
    return regionUtils.formatNumber({ value: result, region });
};
